// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// context
import { ContextBanner, ContextUser, ContextUserEntity } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// types
import {
	CsvField,
	CsvRow,
	Entity,
	EntityLoanPayload,
	EntityPayload,
	ErrorCause,
	ExcelSheet,
	Feedback,
	FormField,
	Loan,
	MetadataField,
	UserPayload,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useEntityForms } from '../../../hooks/useEntityForms';
import { useFetch } from '../../../hooks/useFetch';
import { usePermissions } from '../../../hooks/usePermissions';

// mui
import { Alert, AlertTitle, Box, Checkbox, FormControlLabel, Grid } from '@mui/material';

// forms
import { FormTableFields } from '../FormTableFields/FormTableFields';

// fields
import { InputFile } from '../../fields/InputFile/InputFile';
import { Select } from '../../fields/Select/Select';

// ui
import { AlertFeedback } from '../../ui/AlertFeedback/AlertFeedback';
import { BannerProps } from '../../ui/Banner/Banner';

// utils
import { isCsvFile, isExcelFile, parseCsv, parseExcel, readCsvFile } from '../../../utils/files';
import { translateError } from '../../../utils/translations';

// props
export interface FormUploadSuppliersProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormUploadSuppliers = ({
	onClose,
	onSuccess,
	setIsLoading,
}: FormUploadSuppliersProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { currencies, getDefaultFields } = useEntityForms();
	const { t } = useTranslation();
	const { getPermissionsDefault } = usePermissions();

	// vars
	const formId = 'form-upload-suppliers';

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { user } = useContext(ContextUser);
	const { userEntity } = useContext(ContextUserEntity);

	// vars
	const isWfa = user?.role === 'WFA';
	const userEntityId = userEntity?.id;
	const fieldsDefault = getDefaultFields({ form: 'SUPPLIER' });

	// state
	const [availableSheets, setAvailableSheets] = useState<ExcelSheet[]>([]);
	const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
	const [csvRows, setCsvRows] = useState<CsvRow[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [entity, setEntity] = useState('');
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [fields, setFields] = useState<CsvField[]>(fieldsDefault);
	const [warnings, setWarnings] = useState<string[]>([]);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedSheet, setSelectedSheet] = useState('');

	// fetch form
	const { fetchRequest: fetchForm } = useFetch({
		isLazy: true,
		isEnabled: Boolean(project?.id && currencies.length),
		url: `${process.env.REACT_APP_API_URL}/forms?entity=${userEntityId || entity}&project=${
			project?.id
		}&name=SUPPLIER&limit=1&orderBy=modified&populate=user&status=PUBLISHED`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data?.[0]) {
				// add metadata prefix to dynamic fields
				const fieldsMetadata: CsvField[] = res.data[0].fields.map((field: FormField) => {
					return {
						...field,
						examples: [],
						header: null,
						name: `metadata-${field.name}`,
					};
				});

				setFields([...fieldsDefault, ...fieldsMetadata]);
			}
		},
	});

	// fetch entity loans
	let urlLoans = `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&fields=id,code,status`;

	if (isWfa && entity) {
		urlLoans += `&entity=${entity}`;
	}

	const { fetchRequest: fetchLoans } = useFetch({
		isLazy: true,
		url: urlLoans,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// fetch project entities
	useFetch({
		isEnabled: Boolean(project && isWfa),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name&type=INTERMEDIARY,PIU,SUB_PIU`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// options
	const optionsLoans = loans.map((loan) => {
		return {
			disabled: loan.status === 'DISABLED',
			label: loan.code,
			value: loan.id,
		};
	});

	const optionsEntities = entities
		.map((entity) => {
			return {
				label: entity.name,
				value: String(entity.id),
			};
		})
		.sort((a, b) => (a.label > b.label ? 1 : -1));

	const optionsHeaders = csvHeaders.map((header: string) => {
		return {
			label: header,
			value: header,
		};
	});

	const optionsSheets = availableSheets.map((sheet) => {
		return {
			label: `${sheet.name} (${sheet.rowCount - 1} ${t('rows').toLowerCase()})`,
			value: sheet.name,
		};
	});

	// handlers
	const handleSelectFile = async (e: ChangeEvent<HTMLInputElement>) => {
		if (e.target.files?.length === 1) {
			const file = e.target.files[0];
			setSelectedFile(file);

			// Clear previous data
			setCsvHeaders([]);
			setCsvRows([]);
			setAvailableSheets([]);
			setSelectedSheet('');
			setFeedback(null);
			setWarnings([]);

			try {
				if (isCsvFile(file.name)) {
					// Handle CSV file
					const text = await readCsvFile(file);
					handleCsv(text);
				} else if (isExcelFile(file.name)) {
					// Handle Excel file
					const result = await parseExcel({ file });
					setAvailableSheets(result.sheets);

					// If only one sheet, auto-select it and load data
					if (result.sheets.length === 1) {
						setSelectedSheet(result.sheets[0].name);
						setCsvHeaders(result.headers);
						setCsvRows(result.rows);
					}
				} else {
					throw new Error('Unsupported file format', {
						cause: {
							id: 'FILE_INVALID_FORMAT',
						},
					});
				}
			} catch (error) {
				const err = error as Error;
				const cause = err.cause as ErrorCause;

				setFeedback({
					alert: {
						children: translateError({ cause, t }),
						severity: 'error',
					},
				});
			}
		}
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		setFeedback(null);

		// set loading
		setIsLoading(true);

		try {
			// define form data
			const form = document.querySelector(`#${formId}`) as HTMLFormElement;
			const fd = new FormData(form);
			const entries = Object.fromEntries(fd.entries());

			if (entries.loan === '') {
				throw new Error('Financing source must be selected.', {
					cause: {
						id: 'FINANCING_SOURCE_NOT_SELECTED',
					},
				});
			}

			// define suppliers
			const suppliers = [];

			// define loans
			const supplierLoans: EntityLoanPayload[] = [];
			const entryLoans = (entries.loans as string).split(',');

			// for each loan entry
			for (let i = 0; i < entryLoans.length; i++) {
				let parent = null;

				if (isWfa && entries.entity) {
					parent = Number(entries.entity);
				} else if (userEntity) {
					parent = userEntity.id;
				}

				// add loan
				supplierLoans.push({
					id: Number(entryLoans[i]),
					parent,
				});
			}

			// for each row
			for (let i = 0; i < csvRows.length; i++) {
				const row = csvRows[i];
				const supplier: Partial<EntityPayload> = {
					isKycVerified: true,
					loans: supplierLoans,
					metadata: [],
					project: project?.id,
					type: 'SUPPLIER',
					users: [],
				};

				const supplierUser: UserPayload = {
					country: project?.country?.id || null,
					email: null,
					firstName: '',
					lastName: '',
					projects: [
						{
							id: project?.id,
							permissions: getPermissionsDefault({ entityType: 'SUPPLIER' }),
						},
					],
					role: 'GENERAL',
				};

				// for each field
				for (let f = 0; f < fields.length; f++) {
					const field = fields[f];

					if (!field.header && field.isRequired) {
						throw new Error('Select corresponding fields for all the required fields.', {
							cause: {
								id: 'BULK_UPLOAD_FIELD_EMPTY',
							},
						});
					}

					if (field.header) {
						let value = row[field.header];

						if (value === '') {
							value = null;
						}

						let key: string;

						// field type validations
						if (value) {
							// validate SELECT
							if (field?.type === 'SELECT') {
								const validOptions = field?.options?.map((option) => option.value);
								if (validOptions && !validOptions.includes(value as string)) {
									throw new Error(
										`The value for the ${
											field.label
										} field must be one of the following: ${validOptions.join(', ')}`,
										{
											cause: {
												id: 'FORM_FIELD_SELECT_INVALID',
												details: {
													label: field.label,
													options: validOptions.join(', '),
												},
											},
										}
									);
								}
							}

							// validate NUMBER
							if (field?.type === 'NUMBER') {
								// remove non numerical values
								value = String(value).replace(/[^\d.]/g, '');

								if (value === '') {
									throw new Error(`The value for the ${field.label} field must be a number`, {
										cause: {
											id: 'FORM_FIELD_NUMBER_INVALID',
											details: {
												label: field.label,
											},
										},
									});
								}

								// define value as number
								value = Number(value);
							}

							// validate CHECKBOX
							if (field?.type === 'CHECKBOX') {
								const trueCheckboxValues = [1, 'true', 'TRUE'];
								const falseCheckboxValues = [0, 'false', 'FALSE'];
								const acceptedCheckboxValues = trueCheckboxValues.concat(falseCheckboxValues);
								const validCheckboxValues = acceptedCheckboxValues.join(', ');

								if (!acceptedCheckboxValues.includes(value as string | number)) {
									throw new Error(
										`The value for ${field.label} must be one of the following: ${validCheckboxValues}`,
										{
											cause: {
												id: 'FORM_FIELD_CHECKBOX_INVALID',
												details: {
													label: field.label,
													options: validCheckboxValues,
												},
											},
										}
									);
								}

								// define value as boolean
								value = Boolean(trueCheckboxValues.includes(value as string));
							}

							// validate DATE
							if (field?.type === 'DATE') {
								// define value as date
								value = new Date(value as Date | string);

								if (value.toString() === 'Invalid Date') {
									throw new Error(`The value for ${field.label} must be a date`, {
										cause: {
											id: 'FORM_FIELD_DATE_INVALID',
											details: {
												label: field.label,
											},
										},
									});
								}
							}

							// validate country
							if (
								field.name === 'metadata-country' &&
								field.isRequired &&
								String(value).length !== 2
							) {
								throw new Error(
									'Countries must be formatted using two-letter ISO 3166-1 alpha-2 codes.',
									{
										cause: {
											id: 'COUNTRY_CODE_INVALID',
										},
									}
								);
							}
						}

						if (field.name.includes('metadata-')) {
							// group metadata
							key = field.name.replace('metadata-', '');
							(supplier.metadata as MetadataField[]).push({
								isProtected: Boolean(field.isProtected),
								label: field.label,
								name: key,
								type: field.type,
								value,
							});
						} else if (field.name.includes('user-')) {
							// group user
							key = field.name.replace('user-', '');
							supplierUser[key as keyof UserPayload] = value;
						} else {
							// set supplier key and value
							key = field.name;
							supplier[key] = value;
						}
					}
				}

				// ensure email or phone is set
				if (!supplierUser.email && !supplierUser.phone) {
					throw new Error('User phone or email is empty.', {
						cause: {
							id: 'USER_PHONE_OR_EMAIL_EMPTY',
						},
					});
				}

				// add supplier user
				if (supplier.users) {
					supplier.users.push(supplierUser);
				}

				// add supplier
				suppliers.push(supplier);
			}

			const fetchPostSuppliers = await fetch(`${process.env.REACT_APP_API_URL}/entities`, {
				method: 'post',
				body: JSON.stringify(suppliers),
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});
			const resPostSuppliers = await fetchPostSuppliers.json();

			if (resPostSuppliers.error) {
				throw new Error(resPostSuppliers.error.message, {
					cause: {
						id: resPostSuppliers.error.id,
					},
				});
			}

			const failedLength = resPostSuppliers.data.failed.length;
			const succeededLength = resPostSuppliers.data.succeeded.length;

			const banner: BannerProps = {
				hasClose: true,
				message: t('alert.suppliersUploadedPartial', {
					failed: failedLength,
					succeeded: succeededLength,
				}),
				severity: 'info',
			};

			const allSucceed = failedLength === 0;
			const allFailed = failedLength === suppliers.length;

			if (onSuccess) {
				onSuccess();
			}

			if (resPostSuppliers.data.failed.length) {
				let msg = t('alert.suppliersUploadedError');
				if (allFailed) {
					msg = t('alert.suppliersUploadedError');
				} else if (succeededLength === 1 && failedLength === 1) {
					msg = t('alert.suppliersUploadedPartialOneSucceededOneFailed');
				} else if (succeededLength === 1) {
					msg = t('alert.suppliersUploadedPartialOneSucceeded', {
						failed: failedLength,
					});
				} else if (failedLength === 1) {
					msg = t('alert.suppliersUploadedPartialOneFailed', {
						succeeded: succeededLength,
					});
				} else {
					msg = t('alert.suppliersUploadedPartial', {
						failed: failedLength,
						succeeded: succeededLength,
					});
				}
				throw new Error(msg, {
					cause: {
						id: 'BULK_UPLOAD_INCOMPLETE',
						res: resPostSuppliers,
					},
				});
			}

			if (allSucceed) {
				banner.message = t('alert.suppliersUploadedSuccess');
				banner.severity = 'success';
			}

			// set banner
			setBanner(banner);

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;
			const errors = [];

			if (cause.id === 'BULK_UPLOAD_INCOMPLETE') {
				for (const err of cause.res.data.failed) {
					if (err.error.id === 'ENTITY_DUPLICATE') {
						errors.push(t('error.entityDuplicate', { id: err.payload.idExternal }));
					}
				}
			}

			setFeedback({
				alert: {
					children: translateError({ cause, t }),
					severity: 'error',
				},
				errors: errors,
				response: cause.res,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleSelectSheet = async (sheetName: string) => {
		if (!selectedFile || !isExcelFile(selectedFile.name)) return;

		// Clear previous data
		setCsvHeaders([]);
		setCsvRows([]);
		setFeedback(null);

		try {
			const result = await parseExcel({ file: selectedFile, sheetName });
			setSelectedSheet(sheetName);
			setCsvHeaders(result.headers);
			setCsvRows(result.rows);
		} catch (error) {
			setFeedback({
				alert: {
					children: t('error.sheetParsingFailed'),
					severity: 'error',
				},
				errors: [t('error.sheetNotFound', { sheetName })],
			});
		}
	};

	const handleCsv = (text: string) => {
		if (text) {
			try {
				const { rows, headers } = parseCsv({ string: text });

				// Validate that we have headers
				if (headers.length === 0) {
					throw new Error('CSV file appears to be empty or invalid', {
						cause: {
							id: 'CSV_EMPTY_OR_INVALID',
						},
					});
				}

				// set csv headers and rows
				setCsvHeaders(headers);
				setCsvRows(rows);

				// Warn if replacement characters were found (encoding issue)
				const warningsNew: string[] = [];
				if (text.includes('\uFFFD')) {
					warningsNew.push(t('warning.csvEncodingIssue'));
				}
				setWarnings(warningsNew);
				setFeedback(null);
			} catch (error) {
				const err = error as Error;
				const cause = err.cause as ErrorCause;
				const errors = [];

				if (cause?.id === 'CSV_EMPTY_OR_INVALID') {
					errors.push(t('error.csvFormatInvalid'));
				}

				setFeedback({
					alert: {
						children: translateError({ cause, t }),
						severity: 'error',
					},
				});

				setWarnings([]);
			}
		}
	};

	// fetch form and loans when entity changes
	useEffect(() => {
		if (!isWfa || (isWfa && entity)) {
			fetchLoans();
			fetchForm();
		}
	}, [entity, fetchForm, fetchLoans, isWfa]);

	// Show sheet selector for Excel files with multiple sheets
	const hasSheetSelect = availableSheets.length > 1;
	const hasData = csvHeaders.length > 0 && csvRows.length > 0;

	let colSizeEntity = 6;
	let colSizeSheet = 6;
	let colSizeFinancingSources = 12;

	if (isWfa && hasSheetSelect) {
		colSizeEntity = 4;
		colSizeSheet = 4;
		colSizeFinancingSources = 4;
	} else if (isWfa || hasSheetSelect) {
		colSizeFinancingSources = 6;
	}

	return (
		<form id={formId} name="formUploadSuppliers" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<InputFile
						hasButton={true}
						helperText={t('acceptedFileFormats', { formats: '.csv, .xlsx, .xls' }) + '. '}
						id={`${formId}-file-picker`}
						inputProps={{ accept: '.csv,.xlsx,.xls' }}
						label={t('selectFile')}
						onChange={handleSelectFile}
						required={true}
					/>
				</Grid>
				{hasSheetSelect && (
					<Grid item xs={12} lg={colSizeSheet}>
						<Select
							id={`${formId}-sheet`}
							label={t('selectSheet')}
							onChange={(e) => {
								const { value } = e.target as HTMLInputElement;
								handleSelectSheet(value);
							}}
							options={optionsSheets}
							required={true}
							value={selectedSheet}
						/>
					</Grid>
				)}
				{isWfa && (
					<Grid item xs={12} lg={colSizeEntity}>
						<Select
							id={`${formId}-entity`}
							label={t('entity')}
							name="entity"
							onChange={(e) => {
								const { value } = e.target as HTMLInputElement;
								setEntity(value);
							}}
							options={optionsEntities}
							required={true}
							value={entity}
						/>
					</Grid>
				)}
				<Grid item xs={12} lg={colSizeFinancingSources}>
					<Select
						hasAllOption={true}
						id={`${formId}-loans`}
						label={t('financingSources')}
						multiple={true}
						name="loans"
						options={optionsLoans}
						required={true}
					/>
				</Grid>
				{hasData && (
					<Grid item xs={12}>
						<FormTableFields
							fields={fields}
							formId={formId}
							options={optionsHeaders}
							rows={csvRows}
							setFields={setFields}
							titleTotal={t('totalSuppliers')}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<FormControlLabel
						control={
							<Checkbox
								defaultChecked={true}
								defaultValue="on"
								id={`${formId}-is-kyc-verified`}
								name="isKycVerified"
								required={true}
							/>
						}
						label={t('formUploadSuppliers.kycAml')}
					/>
				</Grid>
				{warnings.length > 0 && (
					<Grid item xs={12}>
						<Alert severity="warning">
							<AlertTitle>{t('warnings')}</AlertTitle>
							<Box component="ul">
								{warnings.map((warning, index) => (
									<Box component="li" key={index}>
										{warning}
									</Box>
								))}
							</Box>
						</Alert>
					</Grid>
				)}
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback feedback={feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
